import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
var TokenizationClientContext = React.createContext({
    updateFrame: function () { },
    submit: function () { },
    tokenServerUrl: undefined,
    setOnTokenReceived: function (callback) { return function () { }; },
    setOnErrorReceived: function (callback) { return function () { }; },
    setOnStateChangeReceived: function (callback) { return function () { }; },
    windowReady: false,
    portReady: false
});
export var TokenizationClientProvider = function (_a) {
    var children = _a.children, tokenServerUrl = _a.tokenServerUrl, developerMode = _a.developerMode, initialFrameSettings = _a.initialFrameSettings, onTokenReceived = _a.onTokenReceived, onStateChangeReceived = _a.onStateChangeReceived, onErrorReceived = _a.onErrorReceived;
    // Get the unboxed value as 'port'
    var portRef = useRef();
    // To useState with a function value, you must return the function from a function
    var _b = useState(function () { return onTokenReceived; }), onTokenReceivedCb = _b[0], setOnTokenReceivedCb = _b[1];
    var _c = useState(function () { return onErrorReceived; }), onErrorReceivedCb = _c[0], setOnErrorReceivedCb = _c[1];
    var _d = useState(function () { return onStateChangeReceived; }), onStateChangeReceivedCb = _d[0], setOnStateChangeReceivedCb = _d[1];
    var _e = useState(false), windowReady = _e[0], setWindowReady = _e[1];
    var _f = useState(false), portReady = _f[0], setPortReady = _f[1];
    var logEvent = function (message) {
        if (!developerMode) {
            return;
        }
        console.log(message);
    };
    var messageReceived = useCallback(function (event) {
        var message = event.data;
        logEvent("Received message : " + JSON.stringify(message));
        switch (message.type) {
            case 'token': {
                if (onTokenReceivedCb) {
                    onTokenReceivedCb(message.data);
                }
                else {
                    console.error("Token received, but the onTokenReceived callback was not\n                                         bound on the provider or the client frame");
                }
                break;
            }
            case 'state': {
                if (onStateChangeReceivedCb) {
                    onStateChangeReceivedCb(message.data);
                }
                else {
                    console.error("StateChange received, but the onStateChangeReceived callback was not\n                                         bound on the provider or the client frame");
                }
                break;
            }
            case 'error': {
                if (onErrorReceivedCb) {
                    onErrorReceivedCb(message.data);
                }
                else {
                    console.error("Error received, but the onErrorReceived callback was not\n                                         bound on the provider or the client frame");
                }
                break;
            }
        }
    }, [onTokenReceivedCb, onErrorReceived]);
    var initializeFrame = useCallback(function (frameInit) {
        logEvent("Initialization request received " + JSON.stringify(frameInit));
        portRef.current.postMessage({ type: 'initialize', data: frameInit });
    }, [portRef]);
    var updateFrame = useCallback(function (frameUpdate) {
        logEvent("Update request received " + JSON.stringify(frameUpdate));
        portRef.current.postMessage({ type: 'update', data: frameUpdate });
    }, [portRef]);
    var submitFrame = useCallback(function () {
        logEvent("Submit request received ");
        portRef.current.postMessage({ type: 'submit' });
    }, [portRef]);
    var windowEventHandler = useCallback(function (event) {
        var tokenServerAsUrl = new URL(tokenServerUrl);
        // Need to verify the origin
        if (event.origin !== tokenServerAsUrl.origin)
            return;
        logEvent("Origin Verified");
        // We need to not rebind the port
        if (event.ports[0]) {
            portRef.current = event.ports[0];
        }
        // Need to bind the message handler
        // before initializing so we don't
        // miss any messages.
        portRef.current.onmessage = messageReceived;
        logEvent("Message handler bound");
        initializeFrame(initialFrameSettings);
        logEvent("Frame Initialized");
        setPortReady(true);
    }, [initialFrameSettings, tokenServerUrl, initializeFrame, messageReceived, portRef, setPortReady]);
    useEffect(function () {
        // We need a reference to the event handler to
        // free it in the callback/cleanup call
        window.addEventListener('message', windowEventHandler);
        logEvent("Event Listener 'message' Added");
        setWindowReady(true);
        // Could set the windowReady to false in the de-init here, but it-should really happen
        // I think..
        return function () { return window.removeEventListener('message', windowEventHandler); };
    }, [windowEventHandler, setWindowReady]);
    return (React.createElement(TokenizationClientContext.Provider, { value: {
            windowReady: windowReady,
            portReady: portReady,
            updateFrame: updateFrame,
            submit: submitFrame,
            tokenServerUrl: tokenServerUrl,
            developerMode: developerMode,
            setOnTokenReceived: function (tokenDataCb) {
                // to set a useState's value to a function
                // you must return the function (overloaded method)
                setOnTokenReceivedCb(function () { return tokenDataCb; });
            },
            setOnErrorReceived: function (errorDataCb) {
                setOnErrorReceivedCb(function () { return errorDataCb; });
            },
            setOnStateChangeReceived: function (stateChangedCb) {
                setOnStateChangeReceivedCb(function () { return stateChangedCb; });
            }
        } }, children));
};
export var useTokenizationClient = function () { return useContext(TokenizationClientContext); };
//# sourceMappingURL=TokenizationClientProvider.js.map